<template>
  <div class="project_variable-selector-container" ref="containerRef">     
    <!-- 选择框头部 -->
    <div class="selector-header" @click="toggleDropdown" :class="{ 'open': dropdownVisible }">
      <div v-if="hasSelectedVariables" class="selected-tags">
        <el-tag
          v-if="selectedVariables[0]"
          closable
          @close="removeSelectedVariable(0)"
          class="variable-tag"
        >
          {{ selectedVariables[0].display_name }}
        </el-tag>
        <!-- 如果还有更多选中的变量，显示 +N -->
        <el-tag
          v-if="selectedVariables.length > 1"
          class="variable-tag count-tag"
        >
          +{{ selectedVariables.length - 1 }}
        </el-tag>
      </div>
      <span v-else class="placeholder">{{ t('message.varableSelectorControl.pleaseSelect') }}</span>
      
      <div 
        class="dropdown-icon" 
        @mouseenter="isHovering = true"
        @mouseleave="isHovering = false"
        @click.stop="handleIconClick"
      >
        <el-icon :class="{ 'reverse': dropdownVisible }">
          <component :is="getIconComponent" />
        </el-icon>
      </div>      
    </div>
    
    <!-- 下拉面板内容 -->
    <div 
      v-show="dropdownVisible" 
      class="selector-dropdown" 
      :class="variableSelectorPopper"
      :style="dropdownStyle"
      @click.stop
    >
      <div class="selector-panel">   
        <!-- 右侧变量列表 -->
        <div class="variable-panel">
          <div class="panel-header">{{ t('message.varableSelectorControl.variableList') }}</div>
          <div class="search-group-row">
            <el-select
              v-model="selectedGroupIds"
              :placeholder="$t('message.realTimeData.selectVariableGroup')"
              size="small"
              multiple
              collapse-tags
              collapse-tags-tooltip
              clearable
              class="group-select"
              @change="handleGroupChange"
            >
              <el-option
                v-for="group in varGroupOptions"
                :key="group.value"
                :label="group.label"
                :value="group.value"
              />
            </el-select>

            <el-input
              v-model="variableSearch"
              :placeholder="$t('message.varableSelectorControl.searchVariable')"
              size="small"
              clearable 
              class="search-input"            
              @keyup.enter="handleSearch"
            >
              <template #append>
                <el-button                 
                  :icon="Search"
                  size="small" 
                  @click="handleSearch"
                  class="search-button"
                />
              </template>
            </el-input>
          </div>
          
          <el-scrollbar class="variable-list">
            <div class="variable-checkbox-group">
              <div
                v-for="variable in filteredVariables"
                :key="getVariableKey(variable)"
                class="variable-item"
              >
                <label class="custom-checkbox">
                  <input 
                    type="checkbox" 
                    :checked="isVariableSelected(variable)"
                    @change="toggleVariableSelection(variable)"                    
                  >                  
                  <span class="black-text">{{ variable.display_name || variable.name || '未知参数' }}</span>
                </label>
              </div>
            </div>
          </el-scrollbar>
          
          <!-- 分页控件 -->
          <div class="pagination">
            <div class="select-all-container">
              <label class="select-all-checkbox">
                <input 
                  type="checkbox" 
                  :checked="isAllSelected"
                  :indeterminate="isIndeterminate"
                  @change="toggleSelectAll"
                >
                <span class="select-all-text">{{ selectAllText }}</span>
              </label>
            </div>
            <el-pagination
              small
              layout="prev, pager, next"
              :total="pagination.total"
              :page-size="pagination.pageSize"
              :current-page="pagination.currentPage"
              @current-change="handlePageChange"
              class="custom-pagination"
            />
            <span class="total-count">
              {{ t('message.varableSelectorControl.totalCount', { total: pagination.total }) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { 
  ref, 
  reactive, 
  computed, 
  onMounted, 
  watch,    
  defineProps, 
  defineEmits, 
  nextTick 
} from 'vue';
import { useEventListener } from '@vueuse/core';
import { projectCollectionsDomainVars, projectCollectionsDomainGroups } from '@/api/ProjectManger';
import { Search, ArrowDown, Close } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import { useI18n } from 'vue-i18n';

// Props 定义
const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true
  },
  collectionId: {
    type: [String, Number],
    required: true
  },
  domainMenuId: {
    type: [String, Number],
    required: true
  },
  domainId: {
    type: [String, Number],
    required: true
  },
  showAllSlave: {
    type: Boolean,
    default: true
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  deviceId: {
    type: [String, Number],
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'change']);

const { t } = useI18n();

// Refs
const containerRef = ref(null);
const dropdownVisible = ref(false);
const variableSearch = ref('');
const variableOptions = ref([]);
const varGroupOptions = ref([]);
const selectedGroupIds = ref([]);
const selectedGroupNames = ref([]);
const selectedDeviceId = ref(props.deviceId);
const selectedVariables = ref([...props.modelValue])
const isHovering = ref(false);

// 响应式数据
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
});

// 计算属性 - 全选相关
const isAllSelected = computed(() => {
  if (filteredVariables.value.length === 0) return false;
  return filteredVariables.value.every(variable => 
    selectedVariables.value.some(v => v.name === variable.name)
  );
});

const isIndeterminate = computed(() => {
  if (filteredVariables.value.length === 0) return false;
  const selectedCount = filteredVariables.value.filter(variable =>
    selectedVariables.value.some(v => v.name === variable.name)
  ).length;
  return selectedCount > 0 && selectedCount < filteredVariables.value.length;
});

const selectAllText = computed(() => {
  if (isAllSelected.value) {
    return t('message.varableSelectorControl.unselectAll');
  } else {
    return t('message.varableSelectorControl.selectAll');
  }
});

// 下拉面板位置计算
const dropdownStyle = computed(() => {
  if (!containerRef.value) return {};
  
  const rect = containerRef.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  
  // 如果下方空间不足，则向上弹出
  if (rect.bottom + 400 > viewportHeight) {
    return {
      bottom: '100%',
      top: 'auto',
      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.15)'
    };
  } else {
    return {
      top: '100%',
      bottom: 'auto',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
    };
  }
});

// 计算属性
const hasSelectedVariables = computed(() => selectedVariables.value.length > 0);

const getIconComponent = computed(() => {
  if (hasSelectedVariables.value && isHovering.value) {
    return Close;
  }
  return ArrowDown;
});

const filteredVariables = computed(() => {
  if (Array.isArray(variableOptions.value) && variableOptions.value.length > 0) {
    return variableOptions.value;
  }
  return [];
});

const variableSelectorPopper = 'variable-selector-popper';

// 方法
const getVariableKey = (variable) => {
  if (variable.var_id) {
    return `var_${variable.var_id}`;
  }
  return `${variable.name}_${variable.device_code}`;
};

const isVariableSelected = (variable) => {
  return selectedVariables.value.some(v => v.name === variable.name);
};

// 全选/取消全选方法
const toggleSelectAll = () => {
  if (isAllSelected.value) {
    // 取消全选：从已选变量中移除当前页的所有变量
    const currentPageVariableNames = new Set(filteredVariables.value.map(v => v.name));
    const newSelected = selectedVariables.value.filter(v => 
      !currentPageVariableNames.has(v.name)
    );
    selectedVariables.value = newSelected;
  } else {
    // 全选：添加当前页的所有变量（去重）
    const currentSelectedNames = new Set(selectedVariables.value.map(v => v.name));
    const variablesToAdd = filteredVariables.value
      .filter(v => !currentSelectedNames.has(v.name))
      .map(v => ({
        name: v.name,
        display_name: v.display_name,
        device_code: v.device_code
      }));
    
    selectedVariables.value = [...selectedVariables.value, ...variablesToAdd];
  }
  
  nextTick(() => {
    emitSelectionChange(selectedVariables.value);
  });
};

const handleIconClick = () => {
  if (hasSelectedVariables.value && isHovering.value) {
    clearAllSelectedVariables();
  } else {
    toggleDropdown();
  }
};

const clearAllSelectedVariables = () => {
  if (!hasSelectedVariables.value) return;
  
  selectedVariables.value = [];
  emitSelectionChange([]);
};

const removeSelectedVariable = (index) => {
  try {
    if (typeof index !== 'number' || index < 0 || index >= selectedVariables.value.length) {
      throw new Error(t('message.varableSelectorControl.invalidIndex'));
    }

    const variable = selectedVariables.value[index];
    if (variable?.name) {
      toggleVariableSelection(variable);
    }
  } catch (error) {    
    ElMessage.warning(error.message || t('message.error.operationFailed'));
  }  
};

const emitSelectionChange = (newSelection) => {
  emit('update:modelValue', newSelection);
  emit('change', newSelection);
};

const toggleVariableSelection = (variable) => {
  const existingIndex = selectedVariables.value.findIndex(v => v.name === variable.name);
  let newSelected;
  
  if (existingIndex > -1) {
    newSelected = selectedVariables.value.filter(v => v.name !== variable.name);
  } else {
    newSelected = [...selectedVariables.value, {
      name: variable.name,
      display_name: variable.display_name,
      device_code: variable.device_code
    }];
  }
  
  selectedVariables.value = newSelected;
  nextTick(() => {
    emitSelectionChange(newSelected);
  });
};

const handleGroupChange = async (groupIds) => {
  try {
    selectedGroupNames.value = groupIds.map(groupId => {
      const selectedOption = varGroupOptions.value.find(option => option.value === groupId);
      return selectedOption ? selectedOption.label : '';
    }).filter(Boolean);
    
    await handleSearch();
  } catch (error) {
    //ElMessage.error(t('message.error.loadGroupFailed'));
    console.error('处理分组变更失败:', error);
  }
};

const handleSearch = async () => {
  pagination.currentPage = 1;  
  await loadVariables(variableSearch.value);
};

const handlePageChange = (page) => {
  pagination.currentPage = page;    
  loadVariables(variableSearch.value);
};

const toggleDropdown = () => {
  dropdownVisible.value = !dropdownVisible.value;
};

// API 调用
const loadVariableGroups = async () => {
  try {
    const params = { 
      projectId: props.projectId,      
      collectionsId: props.collectionId,
      domainParentId: props.domainMenuId,
      domainId: props.domainId, 
      companyId: 0,
      name: undefined,       
      pageNum: 1,
      pageSize: 1000,
    };

    const res = await projectCollectionsDomainGroups(params);
    
    if (res.success) {
      varGroupOptions.value = [
        { value: -1, label: t('message.realTimeData.allGroups') },
        ...res.data.list.map(group => ({
          value: group.id,
          label: group.groupName
        })).filter(group => group.label)
      ];

      if (varGroupOptions.value.length > 0) {
        selectedGroupIds.value = [-1];
        selectedGroupNames.value = [t('message.realTimeData.allGroups')];
      }
    }    
  } catch (error) {
    console.error('加载变量分组失败:', error);
   // ElMessage.error(t('message.error.loadGroupFailed'));
  }
};

const loadVariables = async (name = null) => {
  try {
    let nameGroups = undefined;
    if (selectedGroupIds.value.length > 0) {
      const validGroupIds = selectedGroupIds.value.filter(id => id !== -1);
      if (validGroupIds.length > 0) {
        nameGroups = validGroupIds.map(groupId => {
          const group = varGroupOptions.value.find(g => g.value === groupId);          
          return group ? group.label : null;
        }).filter(Boolean);
      }
    }

    const params = { 
      projectId: props.projectId,      
      collectionsId: props.collectionId,
      domainParentId: props.domainMenuId,
      domainId: props.domainId, 
      companyId: 0,
      groupNames: nameGroups?.length > 0 ? nameGroups : undefined,
      name: undefined,       
      display_name: name || undefined,
      pageNum: pagination.currentPage,
      pageSize: pagination.pageSize,
    };
  
    const res = await projectCollectionsDomainVars(params);
    pagination.total = 0;
    
    if (res.success) {   
      const convertedData = convertToParameterData(res);
      variableOptions.value = convertedData.data;      
      pagination.total = convertedData.totalCount;

      console.log('原始接口数据:', res.data);
      console.log('转换后数据:', convertedData);
      console.log('变量数量:', convertedData.data.length);
      
      if (convertedData.data.length <= 0) {
        ElMessage.warning(t('message.varableSelectorControl.noVariableData'));
      }
    } else {
      ElMessage.warning(res?.msg || t('message.reaTimePlot.loadVariableListFailed'));
    }
  } catch (error) {
    ElMessage.error(t('message.reaTimePlot.loadVariableListFailed'));    
  }
};

// 数据转换方法
const convertToParameterData = (res) => {
  console.log('开始转换数据，原始响应:', res);  
  if (!res?.data?.list) {
    console.log('无数据可转换');
    return {
      data: [],
      totalCount: 0
    };
  }

  try {
    const convertedData = res.data.list.flatMap(item => {
      console.log('处理分组:', item.group_name, '参数数量:', item.list_parm?.length);
      
      if (!item.list_parm || item.list_parm.length === 0) {
        return [];
      }
      
      return item.list_parm.map(param => {
        const variable = {
          groupName: item.group_name || '未分组',
          groupSort: item.group_sort || 0,
          name: param.var_name || '未知参数',
          display_name: param.display_name || param.var_name || '未知参数',
          device_code: param.device_code || '',
          varSort: param.var_sort || 0,
          var_id: param.var_id,
          id: param.var_id
        };
        console.log('转换变量:', variable.name, variable.display_name);
        return variable;
      }).filter(param => param.name !== '未知参数');
    });

    console.log('转换完成，总变量数:', convertedData.length);
    
    return {
      data: convertedData,
      totalCount: res.data.totalCount || convertedData.length
    };
  } catch (error) {
    console.error('数据转换错误:', error);
    return {
      data: [],
      totalCount: 0
    };
  }
};

// 生命周期和监听
useEventListener('click', (e) => {
  if (containerRef.value && !containerRef.value.contains(e.target)) {
    dropdownVisible.value = false;
  }
});

onMounted(async () => {
  await new Promise(resolve => setTimeout(resolve, 100));
  await loadVariableGroups();
  normalizeSelectedVariables();
  await loadVariables();
});

const normalizeSelectedVariables = () => {
  if (Array.isArray(props.modelValue)) {
    selectedVariables.value = props.modelValue.map(item => {
      if (typeof item === 'string') {
        return { name: item, display_name: item,device_code:item };
      }
      return item;
    });
  }
};

// 监听器
watch(() => props.deviceId, (newVal) => {
  selectedDeviceId.value = newVal;
  variableOptions.value = [];
  selectedVariables.value = [];  
});

watch(() => props.modelValue, () => {
  normalizeSelectedVariables();
});
</script>

<style scoped>
.project_variable-selector-container {
  width: 100%;
  position: relative;
  display: inline-block;
}

.selector-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid var(--el-border-color);
  border-radius: 4px;
  cursor: pointer;
  background-color: #fff;
  user-select: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  min-height: 32px;
}

.selector-header.open {
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
}

.selector-header .placeholder {
  color: #909399;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: calc(100% - 24px);
  flex: 1;
}

.variable-tag {
  margin-right: 4px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selector-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  border: 1px solid var(--el-border-color);
  border-top: none;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 999;
  max-height: 400px;
  overflow: hidden;
}

.variable-selector-popper {
  width: 600px !important;
}

.selector-panel {
  display: flex;
  height: 300px;
}

.variable-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 8px 12px;
  font-weight: bold;
  background-color: var(--el-fill-color-light);
  border-bottom: 1px solid var(--el-border-color);
  color: black !important;
}

.search-group-row {
  display: flex;
  gap: 8px;
  padding: 8px;
  align-items: center;
}

.search-group-row .search-input {
  flex: 1;
  margin: 0;
}

.search-group-row .group-select {
  width: 150px;
  margin: 0;
}

.variable-list {
  flex: 1;
}

.variable-item {
  padding: 8px 16px;
}

.custom-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.custom-checkbox input {
  margin-right: 8px;
}

.black-text {
  color: black !important;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-top: 1px solid var(--el-border-color);
}

.select-all-container {
  flex-shrink: 0;
  margin-right: 15px;
}

.select-all-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 12px;
  color: #606266;
}

.select-all-checkbox input {
  margin-right: 6px;
}

.select-all-text {
  white-space: nowrap;
}

.total-count {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin-right: 10px;
  white-space: nowrap;
}

/* 分页组件样式修复 */
:deep(.custom-pagination) {
  color: #606266;
  flex: 1;
  display: flex;
  justify-content: center;
}

:deep(.custom-pagination .btn-prev),
:deep(.custom-pagination .btn-next),
:deep(.custom-pagination .el-pager li) {
  background-color: #fff;
  border: 1px solid #ddd;
  color: #606266;
  border-radius: 4px;
  margin: 0 2px;
  min-width: 32px;
  height: 32px;
  line-height: 30px;
  font-size: 12px;
}

:deep(.custom-pagination .btn-prev:hover),
:deep(.custom-pagination .btn-next:hover),
:deep(.custom-pagination .el-pager li:hover) {
  background-color: #f5f7fa;
  border-color: #c0c4cc;
  color: #606266;
}

:deep(.custom-pagination .btn-prev:disabled),
:deep(.custom-pagination .btn-next:disabled) {
  background-color: #f5f7fa;
  border-color: #e4e7ed;
  color: #c0c4cc;
  cursor: not-allowed;
}

:deep(.custom-pagination .el-pager li.is-active) {
  background-color: #409eff;
  border-color: #409eff;
  color: #fff;
  font-weight: 600;
}

:deep(.custom-pagination .el-pager li.is-active:hover) {
  background-color: #66b1ff;
  border-color: #66b1ff;
  color: #fff;
}

/* 确保分页数字在白色背景下可见 */
:deep(.custom-pagination .el-pager li:not(.is-active)) {
  color: #606266 !important;
}

:deep(.custom-pagination .el-pager li:not(.is-active):hover) {
  color: #409eff !important;
}

/* 图标颜色调整 */
:deep(.custom-pagination .el-icon) {
  color: #606266;
}

:deep(.custom-pagination .btn-prev:hover .el-icon),
:deep(.custom-pagination .btn-next:hover .el-icon) {
  color: #409eff;
}

:deep(.custom-pagination .btn-prev:disabled .el-icon),
:deep(.custom-pagination .btn-next:disabled .el-icon) {
  color: #c0c4cc;
}

.dropdown-icon {
  margin-left: auto;
  padding-left: 8px;
  display: flex;
  align-items: center;
  transition: transform 0.3s;
}

.dropdown-icon .el-icon {
  transition: transform 0.3s;
  color: var(--el-text-color-secondary);
}

.dropdown-icon .reverse {
  transform: rotate(180deg);
}

.dropdown-icon:hover .el-icon {
  color: var(--el-color-danger);
}

.height-40 {
  height: 40px;
}

.height-40 .selector-header {
  height: 100%;
  min-height: 100%;
  line-height: 38px;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .pagination {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .select-all-container {
    margin-right: 0;
    order: 1;
  }
  
  .custom-pagination {
    order: 3;
    width: 100%;
    justify-content: center;
    margin-top: 8px;
  }
  
  .total-count {
    order: 2;
    margin-right: 0;
  }
}
</style>